import { useMemo } from "react";
import { getRequiredCaseInfoFields } from "../utils/accessioningClientRequirements";
import type { DateOnlyString } from "../utils/dateOnly";

interface PatientValues {
  firstName: string;
  lastName: string;
  dateOfBirth: DateOnlyString | null;
  sex: string;
  medicalRecordNumber: string;
}

interface InsuranceValues {
  insuranceProvider: string;
  planType: string;
  policyNumber: string;
}

interface CaseValues {
  accountId: string;
  locationId: string;
  physicianId: string;
  dateReceived: DateOnlyString | null;
  dateCollected: DateOnlyString | null;
  procedureId: string;
  accessioningType: string;
  externalCaseNumber: string;
  consultationReason: string;
}

interface SpecimenValues {
  sourceId: string;
}

interface FormValues {
  patient: PatientValues;
  case: CaseValues & {
    primaryInsurance: InsuranceValues;
    secondaryInsurance?: InsuranceValues;
    specimens: SpecimenValues[];
  };
}

export interface MissingFieldsResult {
  patient: string[];
  case: string[];
  insurance: string[];
  specimen: { index: number; fields: string[] }[];
  suggestedErrorTypes: string[];
  totalMissing: number;
  hasMissingPatient: boolean;
  hasMissingCase: boolean;
  hasMissingInsurance: boolean;
  hasMissingSpecimen: boolean;
}

/**
 * Hook to detect missing required fields and suggest relevant error types
 */
export function useMissingFields(
  values: FormValues | null
): MissingFieldsResult {
  // Create a serialized key of all relevant fields to use as dependency
  // This ensures the memo recomputes when any form value changes
  const dependencyKey = JSON.stringify({
    patient: values?.patient,
    caseFields: {
      accountId: values?.case?.accountId,
      locationId: values?.case?.locationId,
      physicianId: values?.case?.physicianId,
      dateReceived: values?.case?.dateReceived,
      dateCollected: values?.case?.dateCollected,
      procedureId: values?.case?.procedureId,
      accessioningType: values?.case?.accessioningType,
      externalCaseNumber: values?.case?.externalCaseNumber,
      consultationReason: values?.case?.consultationReason,
    },
    insurance: values?.case?.primaryInsurance,
    specimens: values?.case?.specimens?.map((s) => s.sourceId),
  });

  return useMemo(() => {
    const result: MissingFieldsResult = {
      patient: [],
      case: [],
      insurance: [],
      specimen: [],
      suggestedErrorTypes: [],
      totalMissing: 0,
      hasMissingPatient: false,
      hasMissingCase: false,
      hasMissingInsurance: false,
      hasMissingSpecimen: false,
    };

    if (!values) return result;

    // Check patient required fields
    const patientRequired: (keyof PatientValues)[] = [
      "firstName",
      "lastName",
      "dateOfBirth",
      "sex",
      "medicalRecordNumber",
    ];

    patientRequired.forEach((field) => {
      const value = values.patient[field];
      if (!value || (typeof value === "string" && !value.trim())) {
        result.patient.push(field);
      }
    });

    const aType = values.case.accessioningType;
    const caseRequired = getRequiredCaseInfoFields(aType);

    caseRequired.forEach((field) => {
      const value = values.case[field];
      if (!value || (typeof value === "string" && !value.trim())) {
        result.case.push(field);
      }
    });

    // Check primary insurance recommended fields (non-blocking per C-783/C-1617)
    // These are tracked for auto-error creation on completion, not for blocking validation
    const insuranceRecommended: (keyof InsuranceValues)[] = [
      "insuranceProvider",
      "planType",
      "policyNumber",
    ];

    const primaryInsurance = values.case.primaryInsurance;
    if (primaryInsurance) {
      insuranceRecommended.forEach((field) => {
        const value = primaryInsurance[field];
        if (!value || (typeof value === "string" && !value.trim())) {
          result.insurance.push(field);
        }
      });
    } else {
      result.insurance = [...insuranceRecommended];
    }

    // Check specimen required fields
    const specimens = values.case.specimens || [];
    specimens.forEach((spec, index) => {
      const missingFields: string[] = [];

      if (!spec.sourceId || !spec.sourceId.trim()) {
        missingFields.push("sourceId");
      }

      if (missingFields.length > 0) {
        result.specimen.push({ index, fields: missingFields });
      }
    });

    // Calculate totals and flags
    result.hasMissingPatient = result.patient.length > 0;
    result.hasMissingCase = result.case.length > 0;
    result.hasMissingInsurance = result.insurance.length > 0;
    result.hasMissingSpecimen = result.specimen.length > 0;

    result.totalMissing =
      result.patient.length +
      result.case.length +
      result.insurance.length +
      result.specimen.reduce((sum, s) => sum + s.fields.length, 0);

    // Determine suggested error types based on what's missing
    // Only use error codes that exist in the database
    const suggestedTypes: string[] = [];

    // Patient, insurance, and case fields all suggest PatData
    if (
      result.hasMissingPatient ||
      result.hasMissingInsurance ||
      result.hasMissingCase
    ) {
      suggestedTypes.push("PatData");
    }

    if (result.hasMissingSpecimen) {
      suggestedTypes.push("SpecNotRcvd");
      suggestedTypes.push("NoSpec");
    }

    result.suggestedErrorTypes = suggestedTypes;

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependencyKey]);
}

/**
 * Get human-readable field name for display
 */
export function getFieldDisplayName(field: string): string {
  const displayNames: Record<string, string> = {
    firstName: "First Name",
    lastName: "Last Name",
    dateOfBirth: "Date of Birth",
    sex: "Sex",
    medicalRecordNumber: "Medical Record Number",
    accountId: "Practice",
    locationId: "Surgical Center",
    physicianId: "Physician",
    dateReceived: "Date Received",
    dateCollected: "Date Collected",
    procedureId: "Procedure",
    insuranceProvider: "Insurance Provider",
    planType: "Plan Type",
    policyNumber: "Policy Number",
    sourceId: "Specimen Source",
    externalCaseNumber: "External Case Number",
    consultationReason: "Consultation Reason",
  };

  return displayNames[field] || field;
}
